
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Calculator,
  Copy,
  MessageCircle,
  ExternalLink,
  Trash2,
  FileText
} from "lucide-react";
import { brl, dt, todayISO } from "@/lib/format";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
const getDraftKey = (userEmail) =>
  userEmail
    ? `ms:fatura:proposta:${userEmail.toLowerCase()}`
    : null;
const emptyDraft = { name: "", phone: "", amount: "1000", rate: "20", firstDate: todayISO() };

function loadDraft(userEmail) {
  try {
    const draftKey = getDraftKey(userEmail);

    if (!draftKey) return null;

    const raw = localStorage.getItem(draftKey);

    if (!raw) return null;

    const d = JSON.parse(raw);

    if (!d || typeof d !== "object") return null;

    return { ...emptyDraft, ...d };
  } catch {
    return null;
  }
}
function saveDraft(userEmail, v) {
  try {
    const draftKey = getDraftKey(userEmail);

    if (!draftKey) return;

    localStorage.setItem(draftKey, JSON.stringify(v));
  } catch {}
}

/**
 * Simulador de proposta de empréstimo.
 * Gera um link público /proposta?... que o cliente pode abrir sem login.
 * Mantém rascunho no localStorage — se fechar sem querer, os campos voltam preenchidos.
 */
export default function ProposalDialog({
  open,
  onOpenChange,
  businessName,
  onClientCreated,
}) {
  const { user } = useAuth();
  const skipNextDraftSave = useRef(false);
  const [name, setName] = useState(emptyDraft.name);
  const [phone, setPhone] = useState(emptyDraft.phone);
  const [amount, setAmount] = useState(emptyDraft.amount);
  const [rate, setRate] = useState(emptyDraft.rate);
  const [firstDate, setFirstDate] = useState(emptyDraft.firstDate);
  const [guaranteeType, setGuaranteeType] = useState("sem_garantia");
  const [restored, setRestored] = useState(false);

  // Ao abrir, tenta restaurar do rascunho
  useEffect(() => {
    if (!open) return;
    const d = loadDraft(user?.email);
    if (d) {
      setName(d.name || "");
      setPhone(d.phone || "");
      setAmount(d.amount || "1000");
      setRate(d.rate || "20");
      setFirstDate(d.firstDate || todayISO());
      // considera "restaurado" se tinha algo digitado além dos defaults
      const hasEdits =
        (d.name && d.name.trim()) || (d.phone && d.phone.trim()) ||
        (d.amount && d.amount !== "1000") || (d.rate && d.rate !== "20");
      setRestored(!!hasEdits);
    }
  }, [open, user?.email]);

  // Salva rascunho enquanto o dialog está aberto
  useEffect(() => {
  if (!open) return;

  if (skipNextDraftSave.current) {
    skipNextDraftSave.current = false;
    return;
  }

  saveDraft(user?.email, {
    name,
    phone,
    amount,
    rate,
    firstDate,
    guaranteeType,
  });
}, [
  open,
  user?.email,
  name,
  phone,
  amount,
  rate,
  firstDate,
  guaranteeType,
]);
  const valorNum = parseFloat(amount) || 0;
  const rateNum = parseFloat(rate) || 0;
  const juros = Math.round(valorNum * (rateNum / 100) * 100) / 100;
  const quitar = Math.round((valorNum + juros) * 100) / 100;

  const buildUrl = () => {
    const params = new URLSearchParams({
      valor: String(valorNum),
      taxa: String(rateNum),
      data: firstDate,
      empresa: businessName || "MS Soluções Financeiras",
    });
    if (name.trim()) params.set("cliente", name.trim());
    return `${window.location.origin}/proposta?${params.toString()}`;
  };
const createClient = async () => {
  const confirmed = window.confirm(
  "O cliente aceitou a proposta e deseja efetivar o empréstimo?"
);

if (!confirmed) {
  return;
}
  if (!name.trim()) {
    toast.error("Informe o nome do cliente.");
    return;
  }

  if (valorNum <= 0) {
    toast.error("Informe um valor de empréstimo válido.");
    return;
  }

  try {
    const payload = {
      name: name.trim(),
      phone: phone.trim(),
      loan_amount: valorNum,
      loan_date: todayISO(),
      interest_rate: rateNum,
      collection_method: "a_vista",
      collection_frequency: "mensal",
      installments_count: 1,
      installment_amount: quitar,
      first_payment_date: firstDate,
      notes: "Cliente efetivado a partir da simulação de proposta.",
      custom_daily_late_fee: null,
    };

    const response = await api.post("/clients", payload);

    toast.success("Cliente efetivado com sucesso!");

    if (onClientCreated) {
      onClientCreated(response.data);
    }

    onOpenChange(false);
  } catch (error) {
    console.error("Erro ao efetivar cliente:", error);

    const message =
      error?.response?.data?.detail ||
      "Não foi possível efetivar o cliente.";

    toast.error(message);
  }
};
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildUrl());
      toast.success("Link da proposta copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const sendProposalWhatsApp = () => {
    const cleaned = (phone || "").replace(/\D/g, "");
    const empresa = businessName || "MS Soluções Financeiras";
    const saudacao = name.trim() ? `Olá ${name.trim()}` : "Olá";
    const msg =
      `${saudacao}! Aqui é ${empresa}.\n\n` +
      `Segue a proposta de empréstimo:\n` +
      `• Valor: ${brl(valorNum)}\n` +
      `• Taxa: ${rateNum}% ao mês\n` +
      `• Juros mensal: ${brl(juros)}\n` +
      `• Para quitar em 1 mês: ${brl(quitar)}\n` +
      `• Primeira data de pagamento: ${dt(firstDate)}\n\n` +
      `Veja os detalhes: ${buildUrl()}`;
    const url = cleaned
      ? `https://wa.me/${cleaned}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };
const sendContractWhatsApp = () => {
  const cleaned = (phone || "").replace(/\D/g, "");
  const empresa = businessName || "MS Soluções Financeiras";
  const cliente = name.trim() || "Cliente";

  const garantiaLabels = {
    sem_garantia: "Sem garantia",
    fianca: "Fiança",
    cheque_caucao: "Cheque-caução",
    hipoteca: "Hipoteca",
    penhor: "Penhor",
    anticrese: "Anticrese",
  };

  const garantia =
    garantiaLabels[guaranteeType] || "Sem garantia";

  const params = new URLSearchParams({
    name: cliente,
    phone: phone || "",
    business_name: empresa,
    valor: String(valorNum),
    taxa: String(rateNum),
    quitar: String(quitar),
    first_date: firstDate || "",
    guarantee_type: guaranteeType || "sem_garantia",
  });

  const pdfUrl =
    `https://mssystem.onrender.com/billing/contract.pdf?${params.toString()}`;

  const msg =
    `*CONTRATO DE EMPRÉSTIMO*\n\n` +
    `Olá, ${cliente}!\n\n` +
    `Segue o contrato de empréstimo da ${empresa}.\n\n` +
    `*DADOS DA OPERAÇÃO*\n` +
    `Valor: ${brl(valorNum)}\n` +
    `Taxa: ${rateNum.toFixed(2)}% ao mês\n` +
    `Valor para quitação: ${brl(quitar)}\n` +
    `Primeiro vencimento: ${dt(firstDate)}\n` +
    `Garantia: ${garantia}\n\n` +
    `*CONTRATO EM PDF*\n` +
    `Acesse o contrato pelo link abaixo:\n\n` +
    `${pdfUrl}\n\n` +
    `Confira os dados e as condições antes da assinatura.`;

  const url = cleaned
    ? `https://wa.me/${cleaned}?text=${encodeURIComponent(msg)}`
    : `https://wa.me/?text=${encodeURIComponent(msg)}`;

  window.open(url, "_blank", "noopener");
};
  const discardDraft = () => {
  skipNextDraftSave.current = true;

  const draftKey = getDraftKey(user?.email);

  if (draftKey) {
    localStorage.removeItem(draftKey);
  }

  // Remove também o antigo rascunho global
  localStorage.removeItem("ms:fatura:proposta");

  setName("");
  setPhone("");
  setAmount("1000");
  setRate("20");
  setFirstDate(todayISO());
  setGuaranteeType("sem_garantia");
  setRestored(false);

  onOpenChange(false);

  toast.info("Rascunho descartado");
};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B132B] border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="proposal-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Calculator className="w-5 h-5 text-blue-400" /> Simular proposta
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Monte uma proposta e envie ao cliente por WhatsApp ou link.
          </DialogDescription>
        </DialogHeader>

        {restored && (
          <div
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            data-testid="prop-draft-banner"
          >
            <span>Rascunho anterior restaurado — dados da última simulação.</span>
            <button
              type="button"
             onClick={discardDraft}
              className="inline-flex items-center gap-1 text-amber-300 underline underline-offset-2 hover:text-amber-100"
              data-testid="prop-draft-discard"
            >
              <Trash2 className="w-3 h-3" /> Descartar
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Nome do cliente</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Opcional"
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="prop-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">WhatsApp</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(22) 98879-1498"
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
                data-testid="prop-phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Valor (R$)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                data-testid="prop-amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Taxa (% ao mês)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus:border-blue-500 font-mono"
                data-testid="prop-rate"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300">Primeira data de pagamento</Label>
            <Input
              type="date"
              value={firstDate}
              onChange={(e) => setFirstDate(e.target.value)}
              className="bg-slate-900/60 border-slate-700 focus:border-blue-500"
              data-testid="prop-date"
            />
          </div>
          <div className="space-y-1.5">
  <label className="text-sm text-slate-300">
    Tipo de garantia
  </label>

  <select
    value={guaranteeType}
    onChange={(e) => setGuaranteeType(e.target.value)}
    className="w-full rounded-md bg-slate-900/60 border border-slate-700 px-3 py-2 text-sm text-white focus:border-blue-500 outline-none"
    data-testid="prop-guarantee-type"
  >
    <option value="sem_garantia">Sem garantia</option>
    <option value="fianca">Fiança</option>
    <option value="cheque_caucao">Cheque-caução</option>
    <option value="hipoteca">Hipoteca</option>
    <option value="penhor">Penhor</option>
    <option value="anticrese">Anticrese</option>
  </select>
</div>

          <div className="glass-card p-4 space-y-2 text-sm" data-testid="prop-preview">
            <div className="flex justify-between">
              <span className="text-slate-400">Valor emprestado</span>
              <span className="font-mono text-slate-100">{brl(valorNum)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Juros do mês ({rateNum}%)</span>
              <span className="font-mono text-amber-300">{brl(juros)}</span>
            </div>
            <div className="pt-2 border-t border-slate-800 flex justify-between">
              <span className="text-slate-400">Só juros (rola p/ próximo mês)</span>
              <span className="font-mono text-amber-200 font-semibold">{brl(juros)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Para quitar em 1 mês</span>
              <span className="font-mono text-emerald-300 font-semibold">{brl(quitar)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Vencimento</span>
              <span className="font-mono text-slate-100">{dt(firstDate)}</span>
            </div>
          </div>
        </div>
<div className="mt-6 rounded-2xl border border-slate-700 bg-white text-slate-900 p-6">
  <div className="text-center border-b border-slate-200 pb-4">
    <h3 className="text-xl font-bold">
      CONTRATO DE EMPRÉSTIMO
    </h3>

    <p className="text-xs text-slate-500 mt-1">
      Prévia para conferência e assinatura das partes
    </p>
  </div>

  <div className="mt-5 space-y-4 text-sm leading-6">
    <p>
      Pelo presente instrumento particular, as partes abaixo identificadas
      ajustam o presente contrato de empréstimo, mediante as condições
      apresentadas nesta simulação.
    </p>

    <div>
      <p>
        <strong>CREDOR:</strong>{" "}
        {businessName || "MS Soluções Financeiras"}
      </p>

      <p>
        <strong>DEVEDOR:</strong>{" "}
        {name.trim() || "A preencher"}
      </p>

      <p>
        <strong>TELEFONE:</strong>{" "}
        {phone.trim() || "A preencher"}
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 1ª – DO OBJETO
      </p>

      <p>
        O CREDOR disponibilizará ao DEVEDOR a quantia de{" "}
        <strong>{brl(valorNum)}</strong>, conforme as condições
        financeiras apresentadas nesta proposta.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 2ª – DOS ENCARGOS
      </p>

      <p>
        Sobre o valor principal incidirá a taxa indicada na simulação,
        correspondente a <strong>{rateNum.toFixed(2)}%</strong>,
        observados os limites e demais requisitos previstos na legislação
        aplicável.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 3ª – DO VALOR PARA QUITAÇÃO
      </p>

      <p>
        Considerando exclusivamente os parâmetros desta simulação, o valor
        estimado para quitação é de{" "}
        <strong>{brl(quitar)}</strong>.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 4ª – DO VENCIMENTO
      </p>

      <p>
        O pagamento deverá ser realizado até{" "}
        <strong>{dt(firstDate)}</strong>, conforme as condições
        apresentadas nesta proposta.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 5ª – DO ATRASO
      </p>

      <p>
        Em caso de atraso, poderão incidir os encargos de mora e demais
        consequências previstas no contrato definitivo, desde que
        expressamente pactuados e permitidos pela legislação aplicável.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 6ª – DA QUITAÇÃO
      </p>

      <p>
        Após o pagamento integral do débito e dos encargos eventualmente
        devidos, será considerada quitada a obrigação correspondente,
        observados os registros e comprovantes de pagamento.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 7ª – DA CONFERÊNCIA DAS INFORMAÇÕES
      </p>

      <p>
        As partes deverão conferir nome, telefone, valores, datas, taxas e
        demais condições antes da assinatura do instrumento definitivo.
      </p>
    </div>

    <div>
      <p className="font-bold">
        CLÁUSULA 8ª – DA PROPOSTA
      </p>

      <p>
        Esta tela constitui uma prévia da operação. A contratação somente
        deverá ser considerada formalizada após a manifestação de vontade
        das partes e a assinatura do instrumento contratual definitivo,
        quando exigível.
      </p>
    </div>

    <div className="pt-4 border-t border-slate-200">
      <p>
        <strong>Valor principal:</strong> {brl(valorNum)}
      </p>

      <p>
        <strong>Taxa simulada:</strong> {rateNum.toFixed(2)}%
      </p>

      <p>
        <strong>Valor estimado para quitação:</strong> {brl(quitar)}
      </p>

      <p>
        <strong>Vencimento:</strong> {dt(firstDate)}
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-10">
      <div className="text-center">
        <div className="border-t border-slate-400 pt-2">
          <p className="font-semibold">
            {businessName || "CREDOR"}
          </p>
          <p className="text-xs text-slate-500">
            Assinatura do Credor
          </p>
        </div>
      </div>

      <div className="text-center">
        <div className="border-t border-slate-400 pt-2">
          <p className="font-semibold">
            {name.trim() || "DEVEDOR"}
          </p>
          <p className="text-xs text-slate-500">
            Assinatura do Devedor
          </p>
        </div>
      </div>
    </div>

    <div className="text-center pt-6 text-xs text-slate-500">
      Local e data: ________________________________
    </div>
  </div>
</div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
  className="bg-blue-600 hover:bg-blue-500 text-white flex-1"
  onClick={createClient}
  data-testid="prop-create-client"
>
  Efetivar cliente
</Button>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={copy}
            data-testid="prop-copy"
          >
            <Copy className="w-4 h-4 mr-2" /> Copiar link
          </Button>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={() => window.open(buildUrl(), "_blank", "noopener")}
            data-testid="prop-preview-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Ver proposta
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1"
            onClick={sendProposalWhatsApp}
            data-testid="prop-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> Enviar por WhatsApp
          </Button>
          <Button
  className="bg-slate-700 hover:bg-slate-600 text-white flex-1"
  onClick={sendContractWhatsApp}
  data-testid="prop-contract-whatsapp"
>
  <FileText className="w-4 h-4 mr-2" /> Enviar contrato
</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
